import { useState } from "react";
import { useCustom, useGetIdentity, useNavigation } from "@refinedev/core";
import {
  Card,
  Col,
  Row,
  Statistic,
  Typography,
  List,
  Tag,
  Spin,
  Progress,
  Avatar,
  Space,
  Button,
  Divider,
  theme,
} from "antd";
import {
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  UserOutlined,
  ShopOutlined,
  PlusOutlined,
  RightOutlined,
  PhoneOutlined,
  CalendarOutlined,
  StarOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useResponsive } from "../../hooks";
import { useSiteContext } from "../../contexts/SiteContext";
import { colors } from "../../config/theme";
import { EmptyState } from "../../components/EmptyState";
import {
  TimeRangePicker,
  getDateRange,
} from "../../components/TimeRangePicker";
import type { TimeRange } from "../../components/TimeRangePicker";
import type { InBasketItem, Lead, Opportunity, Customer } from "../../types";

const { Title, Text } = Typography;

/**
 * CRM Dashboard widget configuration.
 * Maps the data sources and routes used by the dashboard widgets.
 */
const DASHBOARD_CONFIG = {
  leads: { resource: "Lead", route: "/Lead" },
  opportunities: { resource: "Opportunity", route: "/Opportunity" },
  customers: { resource: "Customer", route: "/Customer" },
} as const;

export const CrmDashboard: React.FC = () => {
  const { data: identity } = useGetIdentity<{ name: string }>();
  const { t } = useTranslation();
  const { push } = useNavigation();
  const { isMobile, isTablet } = useResponsive();
  const { selectedSiteId, isEnabled: siteScopingEnabled } = useSiteContext();
  const { token: themeToken } = theme.useToken();
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const dateRange = getDateRange(timeRange);

  // Build site filter query param for dashboard API calls
  const siteQuery =
    siteScopingEnabled && selectedSiteId ? { siteId: selectedSiteId } : {};

  // Responsive values
  const pagePadding = isMobile ? 12 : isTablet ? 16 : 24;
  const statFontSize = isMobile ? 24 : isTablet ? 28 : 32;
  const gutter: [number, number] = isMobile ? [8, 8] : [16, 16];

  // Fetch leads (Recent)
  const { data: leadsData, isLoading: leadsLoading } = useCustom<{
    data: Lead[];
    total: number;
  }>({
    url: `/items/${DASHBOARD_CONFIG.leads.resource}`,
    method: "get",
    config: {
      query: {
        pageSize: 5,
        orderBy: "created_on",
        orderDesc: true,
        ...siteQuery,
        ...dateRange,
      },
    },
  });

  // Fetch opportunities (Top Value for "Top Opportunities" and Pipeline proxy)
  const { data: opportunitiesData, isLoading: opportunitiesLoading } =
    useCustom<{
      data: Opportunity[];
      total: number;
    }>({
      url: `/items/${DASHBOARD_CONFIG.opportunities.resource}`,
      method: "get",
      config: {
        query: {
          pageSize: 20, // Fetch more for better pipeline stats
          orderBy: "amount",
          orderDesc: true,
          ...siteQuery,
          ...dateRange,
        },
      },
    });

  // Fetch customers
  const { data: customersData, isLoading: customersLoading } = useCustom<{
    data: Customer[];
    total: number;
  }>({
    url: `/items/${DASHBOARD_CONFIG.customers.resource}`,
    method: "get",
    config: {
      query: {
        pageSize: 5,
        ...siteQuery,
        ...dateRange,
      },
    },
  });

  // Fetch pending tasks
  const { data: inbasketData, isLoading: inbasketLoading } = useCustom<{
    items: InBasketItem[];
    totalCount: number;
  }>({
    url: "/workflow/inbasket",
    method: "get",
    config: {
      query: {
        pageSize: 5,
        state: "Active",
      },
    },
  });

  const leads = leadsData?.data?.data || [];
  const totalLeads = leadsData?.data?.total || 0;
  const opportunities = opportunitiesData?.data?.data || [];
  const totalOpportunities = opportunitiesData?.data?.total || 0;
  const totalCustomers = customersData?.data?.total || 0;
  const pendingTasks = inbasketData?.data?.items || [];
  const totalTasks = inbasketData?.data?.totalCount || 0;

  // Calculate pipeline stats
  const pipelineStages = [
    {
      key: "qualification",
      label: t("stage.qualification"),
      color: colors.primary,
    },
    {
      key: "needsAnalysis",
      label: t("stage.needsAnalysis"),
      color: colors.info,
    },
    { key: "proposal", label: t("stage.proposal"), color: colors.secondary },
    {
      key: "negotiation",
      label: t("stage.negotiation"),
      color: colors.warning,
    },
    {
      key: "closedWon",
      label: t("stage.closedWon"),
      color: colors.accent,
      state: "Closed Won",
    },
  ];

  const pipelineStats = pipelineStages.map((stage) => {
    const itemsInStage = opportunities.filter((o) => {
      if (stage.key === "closedWon")
        return o.state === "Closed Won" || o.state === "Won";
      return (
        o.stage?.toLowerCase() === stage.key.toLowerCase() ||
        o.stage === stage.label
      );
    });
    const value = itemsInStage.reduce((sum, o) => sum + (o.amount || 0), 0);
    return {
      ...stage,
      count: itemsInStage.length,
      value,
      percent:
        totalOpportunities > 0
          ? (itemsInStage.length / totalOpportunities) * 100
          : 0,
    };
  });

  const totalPipelineValue = opportunities.reduce(
    (sum, opp) => sum + (opp.amount || 0),
    0,
  );

  // Calculate conversion rate (leads converted to customers)
  const conversionRate =
    totalLeads > 0
      ? Math.round(
          (totalCustomers / (totalLeads + totalCustomers)) * 100 * 10,
        ) / 10
      : 0;

  const getPriorityColor = (priority?: string) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "red";
      case "medium":
        return "orange";
      case "low":
        return "green";
      default:
        return "default";
    }
  };

  const getStateColor = (state?: string) => {
    switch (state?.toLowerCase()) {
      case "new":
        return "blue";
      case "qualified":
        return "cyan";
      case "contacted":
        return "purple";
      case "won":
      case "closed won":
        return "green";
      case "lost":
      case "closed lost":
        return "red";
      default:
        return "default";
    }
  };

  // Translate state/stage names
  const translateState = (
    state?: string,
    stateLabel?: string,
    fallback?: string,
  ) => {
    // If stateLabel is provided and not the same as state, use it (already translated from API)
    if (stateLabel && stateLabel !== state) {
      return stateLabel;
    }
    // Otherwise, translate using i18n
    const valueToTranslate = state || fallback;
    if (!valueToTranslate) return "-";

    const stateKey = valueToTranslate.toLowerCase().replace(/\s+/g, "_");
    const translationKey = `status.${stateKey}`;
    const stageKey = `lists.opportunitySalesStage.${stateKey}`;
    // Try status first, then stage
    const translated = t(translationKey, "");
    if (translated) return translated;
    const stageTranslated = t(stageKey, "");
    if (stageTranslated) return stageTranslated;
    // Fallback to original
    return valueToTranslate;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div
      style={{
        padding: pagePadding,
        background: themeToken.colorBgLayout,
        minHeight: "100vh",
      }}
    >
      {/* Welcome Header */}
      <Row
        justify="space-between"
        align={isMobile ? "top" : "middle"}
        style={{ marginBottom: isMobile ? 16 : 24 }}
        gutter={[0, 12]}
      >
        <Col xs={24} sm={24} md={12}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            {t("dashboard.welcomeBack", { name: identity?.name || "User" })}
          </Title>
          <Text type="secondary">{t("dashboard.overview")}</Text>
        </Col>
        <Col
          xs={24}
          sm={24}
          md={12}
          style={{ textAlign: isMobile ? "left" : "right" }}
        >
          <Space wrap>
            <TimeRangePicker
              value={timeRange}
              onChange={setTimeRange}
              compact={isMobile}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => push(`${DASHBOARD_CONFIG.leads.route}/create`)}
              block={isMobile}
            >
              {t("dashboard.newLead")}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={gutter} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} lg={6}>
          <Card
            hoverable
            onClick={() => push(DASHBOARD_CONFIG.leads.route)}
            size={isMobile ? "small" : "default"}
            style={{ borderTop: `3px solid ${colors.primary}` }}
          >
            <Statistic
              title={
                <Space size={4}>
                  <TeamOutlined style={{ color: colors.primary }} />
                  {isMobile
                    ? t("dashboard.leads", "Leads")
                    : t("dashboard.totalLeads")}
                </Space>
              }
              value={totalLeads}
              valueStyle={{ color: colors.primary, fontSize: statFontSize }}
              loading={leadsLoading}
            />
            {!isMobile && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("dashboard.leadsThisMonth", { count: leads.length })}
                </Text>
                {timeRange !== "all" && (
                  <Text
                    style={{
                      fontSize: 11,
                      color: colors.accent,
                      display: "block",
                    }}
                  >
                    <ArrowUpOutlined /> {leads.length}{" "}
                    {t("dashboard.inPeriod", "in period")}
                  </Text>
                )}
              </>
            )}
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card
            hoverable
            onClick={() => push(DASHBOARD_CONFIG.opportunities.route)}
            size={isMobile ? "small" : "default"}
            style={{ borderTop: `3px solid ${colors.accent}` }}
          >
            <Statistic
              title={
                <Space size={4}>
                  <RiseOutlined style={{ color: colors.accent }} />
                  {isMobile
                    ? t("dashboard.opps", "Opps")
                    : t("dashboard.opportunities")}
                </Space>
              }
              value={totalOpportunities}
              valueStyle={{ color: colors.accent, fontSize: statFontSize }}
              loading={opportunitiesLoading}
            />
            {!isMobile && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("dashboard.pipelineValue")}:{" "}
                  {formatCurrency(totalPipelineValue)}
                </Text>
                {timeRange !== "all" && (
                  <Text
                    style={{
                      fontSize: 11,
                      color: colors.accent,
                      display: "block",
                    }}
                  >
                    <ArrowUpOutlined /> {opportunities.length}{" "}
                    {t("dashboard.inPeriod", "in period")}
                  </Text>
                )}
              </>
            )}
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card
            hoverable
            onClick={() => push(DASHBOARD_CONFIG.customers.route)}
            size={isMobile ? "small" : "default"}
            style={{ borderTop: `3px solid ${colors.secondary}` }}
          >
            <Statistic
              title={
                <Space size={4}>
                  <UserOutlined style={{ color: colors.secondary }} />
                  {isMobile
                    ? t("dashboard.customers", "KH")
                    : t("dashboard.totalCustomers")}
                </Space>
              }
              value={totalCustomers}
              valueStyle={{ color: colors.secondary, fontSize: statFontSize }}
              loading={customersLoading}
            />
            {!isMobile && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("dashboard.conversionRate")}: {conversionRate}%
                </Text>
                {timeRange !== "all" && (
                  <Text
                    style={{
                      fontSize: 11,
                      color: colors.accent,
                      display: "block",
                    }}
                  >
                    <ArrowUpOutlined /> {totalCustomers}{" "}
                    {t("dashboard.inPeriod", "in period")}
                  </Text>
                )}
              </>
            )}
          </Card>
        </Col>
        <Col xs={12} sm={12} lg={6}>
          <Card
            hoverable
            onClick={() => push("/inbasket")}
            size={isMobile ? "small" : "default"}
            style={{ borderTop: `3px solid ${colors.warning}` }}
          >
            <Statistic
              title={
                <Space size={4}>
                  <ClockCircleOutlined style={{ color: colors.warning }} />
                  {isMobile
                    ? t("dashboard.tasks", "Tasks")
                    : t("dashboard.pendingTasks")}
                </Space>
              }
              value={totalTasks}
              valueStyle={{ color: colors.warning, fontSize: statFontSize }}
              loading={inbasketLoading}
            />
            {!isMobile && (
              <>
                <Divider style={{ margin: "12px 0" }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t("dashboard.tasksToday")}
                </Text>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Sales Pipeline & Top Opportunities */}
      <Row gutter={gutter} style={{ marginBottom: isMobile ? 8 : 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <RiseOutlined />
                {t("dashboard.salesPipeline")}
              </Space>
            }
            loading={opportunitiesLoading}
            style={{ height: "100%" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {pipelineStats.map((stage) => (
                <div key={stage.key}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <Text>{stage.label}</Text>
                    <Text strong>{formatCurrency(stage.value)}</Text>
                  </div>
                  <Progress
                    percent={stage.percent}
                    strokeColor={stage.color}
                    showInfo={false}
                    size="small"
                  />
                </div>
              ))}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <StarOutlined />
                {t("dashboard.topOpportunities")}
              </Space>
            }
            extra={
              <Button
                type="link"
                onClick={() => push(DASHBOARD_CONFIG.opportunities.route)}
              >
                {t("dashboard.viewAll")} <RightOutlined />
              </Button>
            }
            styles={{ body: { padding: 0 } }}
            style={{ height: "100%" }}
          >
            <Spin spinning={opportunitiesLoading}>
              <List
                dataSource={opportunities.slice(0, 5)}
                renderItem={(item) => (
                  <List.Item
                    style={{ padding: "12px 24px", cursor: "pointer" }}
                    onClick={() =>
                      push(`${DASHBOARD_CONFIG.opportunities.route}/${item.id}`)
                    }
                    extra={
                      <div style={{ textAlign: "right" }}>
                        <Text strong style={{ display: "block" }}>
                          {formatCurrency(item.amount || 0)}
                        </Text>
                        <Tag color={getStateColor(item.state)}>
                          {translateState(
                            item.state,
                            item.stateLabel,
                            item.stage,
                          )}
                        </Tag>
                      </div>
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          style={{ backgroundColor: colors.warning }}
                          icon={<StarOutlined />}
                        />
                      }
                      title={<Text strong>{item.name}</Text>}
                      description={
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.customerName ||
                            item.organizationName ||
                            t("common.noData")}
                        </Text>
                      }
                    />
                  </List.Item>
                )}
                locale={{
                  emptyText: (
                    <EmptyState
                      title={t("dashboard.noOpportunities")}
                      showCreateButton={false}
                    />
                  ),
                }}
              />
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Recent Leads & Pending Tasks */}
      <Row gutter={gutter}>
        {/* Recent Leads */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <TeamOutlined />
                {t("dashboard.recentLeads")}
              </Space>
            }
            extra={
              <Button
                type="link"
                onClick={() => push(DASHBOARD_CONFIG.leads.route)}
              >
                {t("dashboard.viewAll")} <RightOutlined />
              </Button>
            }
            styles={{ body: { padding: 0 } }}
          >
            <Spin spinning={leadsLoading}>
              <List
                dataSource={leads}
                renderItem={(lead: Lead) => (
                  <List.Item
                    style={{ padding: "12px 24px", cursor: "pointer" }}
                    onClick={() =>
                      push(`${DASHBOARD_CONFIG.leads.route}/${lead.id}`)
                    }
                    extra={
                      lead.priority ? (
                        <Tag color={getPriorityColor(lead.priority)}>
                          {lead.priority}
                        </Tag>
                      ) : null
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          style={{ backgroundColor: colors.primary }}
                          icon={<UserOutlined />}
                        />
                      }
                      title={
                        <Space>
                          <Text strong>{lead.name || lead.contactName}</Text>
                          {lead.state && (
                            <Tag
                              color={getStateColor(lead.state)}
                              style={{ fontSize: 11 }}
                            >
                              {translateState(lead.state, lead.stateLabel)}
                            </Tag>
                          )}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          {(lead.companyName || lead.organizationName) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              <ShopOutlined style={{ marginRight: 4 }} />
                              {lead.companyName || lead.organizationName}
                            </Text>
                          )}
                          <Space
                            size="small"
                            split={<Divider type="vertical" />}
                          >
                            {lead.contactPhone && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                <PhoneOutlined style={{ marginRight: 4 }} />
                                {lead.contactPhone}
                              </Text>
                            )}
                            {lead.leadSource && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {lead.leadSource}
                              </Text>
                            )}
                            {lead.industry && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {lead.industry}
                              </Text>
                            )}
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
                locale={{
                  emptyText: (
                    <EmptyState resource="Lead" showCreateButton={false} />
                  ),
                }}
              />
            </Spin>
          </Card>
        </Col>

        {/* Pending Tasks */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                {t("dashboard.pendingTasks")}
              </Space>
            }
            extra={
              <Button type="link" onClick={() => push("/inbasket")}>
                {t("dashboard.viewAll")} <RightOutlined />
              </Button>
            }
            styles={{ body: { padding: 0 } }}
          >
            <Spin spinning={inbasketLoading}>
              <List
                dataSource={pendingTasks}
                renderItem={(task: InBasketItem) => {
                  const isOverdue =
                    task.dueDate && new Date(task.dueDate) < new Date();
                  return (
                    <List.Item
                      style={{ padding: "12px 24px", cursor: "pointer" }}
                      onClick={() => push("/inbasket")}
                      extra={
                        <Tag
                          color={
                            task.state === "Active"
                              ? isOverdue
                                ? "red"
                                : "orange"
                              : "default"
                          }
                        >
                          {isOverdue
                            ? t("dashboard.overdue")
                            : task.state || t("status.pending")}
                        </Tag>
                      }
                    >
                      <List.Item.Meta
                        avatar={
                          <Avatar
                            style={{
                              backgroundColor: isOverdue
                                ? colors.error
                                : colors.warning,
                            }}
                            icon={<CheckCircleOutlined />}
                          />
                        }
                        title={
                          <Text strong type={isOverdue ? "danger" : undefined}>
                            {task.sourceItemName || task.name}
                          </Text>
                        }
                        description={
                          <Space direction="vertical" size={0}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t("dashboard.activity")}: {task.name}
                            </Text>
                            {task.sourceItemType && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {t("dashboard.itemType")}: {task.sourceItemType}
                              </Text>
                            )}
                            {task.dueDate && (
                              <Text
                                type={isOverdue ? "danger" : "secondary"}
                                style={{ fontSize: 12 }}
                              >
                                <CalendarOutlined style={{ marginRight: 4 }} />
                                {t("dashboard.due", {
                                  date: new Date(
                                    task.dueDate,
                                  ).toLocaleDateString("vi-VN"),
                                })}
                              </Text>
                            )}
                          </Space>
                        }
                      />
                    </List.Item>
                  );
                }}
                locale={{
                  emptyText: (
                    <EmptyState
                      title={t("dashboard.noPendingTasks")}
                      showCreateButton={false}
                    />
                  ),
                }}
              />
            </Spin>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
